import { useState, useEffect, useRef, useCallback, FC } from 'react';
import {
  Smartphone,
  RefreshCw,
  LogOut,
  Send,
  Clock,
  Activity,
  Phone,
  QrCode,
  Terminal,
  AlertTriangle,
  MessageSquare,
  CheckCircle2,
  Battery,
  Plus,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { Drawer } from '../ui/Drawer';
import { Spinner } from '../ui/Spinner';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { DataTable, Column } from '../widgets/DataTable';
import { StepList } from '../widgets/StepList';
import {
  fetchWhatsAppStatus,
  requestWhatsAppConnect,
  requestWhatsAppDisconnect,
  fetchWhatsAppEvents,
  logWhatsAppEvent,
  fetchWhatsAppInstances,
  createWhatsAppInstance,
  connectWhatsAppInstance,
  disconnectWhatsAppInstance,
  deleteWhatsAppInstance,
  WhatsAppStatus,
  WhatsAppSessionEvent,
  WhatsAppInstance,
} from '../../api/whatsapp';
import { fetchCadenceTelemetry, QueueCadenceTelemetry } from '../../api/queue';
import {
  MetricGridSkeleton,
  WhatsAppConnectionCardSkeleton,
} from '../ui/LoadingSkeleton';

export interface WhatsAppViewProps {
  initialStatus?: WhatsAppStatus | null;
  initialInstances?: WhatsAppInstance[] | null;
  isLoading?: boolean;
  onStatusChange?: (status: WhatsAppStatus) => void;
}

export const WhatsAppView: FC<WhatsAppViewProps> = ({
  initialStatus = null,
  initialInstances = null,
  isLoading,
  onStatusChange,
}) => {
  const { token } = useAuth();
  const { success, error, info } = useToast();

  const [status, setStatus] = useState<WhatsAppStatus | null>(initialStatus);
  const [instances, setInstances] = useState<WhatsAppInstance[]>(() => {
    if (initialInstances) return initialInstances;
    if (initialStatus) {
      return [
        {
          id: 'inst-default',
          tenant_id: 'default',
          instance_name: initialStatus.instance_name || 'affiliator_principal',
          phone_number: initialStatus.phone_number,
          status: initialStatus.connected ? 'CONNECTED' : 'DISCONNECTED',
          battery_level: 95,
          daily_sent_count: 0,
          daily_quota: 50,
          created_at: new Date().toISOString(),
        },
      ];
    }
    return [];
  });
  const [qrCodeBase64, setQrCodeBase64] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);

  // Pool de chips: Estado para modal/drawer de novo chip
  const [isNewChipDrawerOpen, setIsNewChipDrawerOpen] = useState(false);
  const [newChipName, setNewChipName] = useState('');
  const [newChipQuota, setNewChipQuota] = useState(50);
  const [newChipQrCode, setNewChipQrCode] = useState<string | null>(null);
  const [newChipPairingCode, setNewChipPairingCode] = useState<string | null>(
    null
  );
  const [isCreatingChip, setIsCreatingChip] = useState(false);

  // Se isLoading for fornecido explicitamente, respeita-o. Caso contrário, se initialStatus for fornecido (ou null em ambiente de render inicial), controla o loading inicial.
  const [isInitialLoading, setIsInitialLoading] = useState(
    isLoading !== undefined ? isLoading : false
  );
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isLogsDrawerOpen, setIsLogsDrawerOpen] = useState(false);

  const [connectionUptimeSeconds, setConnectionUptimeSeconds] =
    useState<number>(0);
  const [cadenceTelemetry, setCadenceTelemetry] =
    useState<QueueCadenceTelemetry | null>(null);
  const wasConnectedRef = useRef<boolean | null>(
    initialStatus ? initialStatus.connected : null
  );
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef<boolean>(false);

  // Histórico de sessões integrado com API
  const [sessionEvents, setSessionEvents] = useState<WhatsAppSessionEvent[]>(
    []
  );

  // Carrega instâncias do pool do backend
  useEffect(() => {
    if (!token) return;
    fetchWhatsAppInstances(token)
      .then((data) => {
        if (data && data.length > 0) {
          setInstances(data);
        }
      })
      .catch((err) => {
        console.debug('Erro ao carregar instâncias do pool:', err);
      });
  }, [token]);

  // Carrega histórico de eventos do backend
  useEffect(() => {
    if (!token) return;
    fetchWhatsAppEvents(token).then((events) => {
      if (events && events.length > 0) {
        setSessionEvents(events);
      }
    });
  }, [token]);

  const handleCreateAndPairNewChip = async () => {
    if (!token) return;
    setIsCreatingChip(true);
    try {
      const created = await createWhatsAppInstance(token, {
        instance_name: newChipName.trim() || undefined,
        daily_quota: Number(newChipQuota) || 50,
      });
      const conn = await connectWhatsAppInstance(token, created.id);
      if (conn.qrcode_base64) {
        setNewChipQrCode(conn.qrcode_base64);
      }
      if (conn.pairing_code) {
        setNewChipPairingCode(conn.pairing_code);
      }
      setInstances((prev) => [...prev, created]);
      success(
        'Novo chip cadastrado no pool! Escaneie o QR Code para conectar.',
        'Chip Criado'
      );
    } catch (err: any) {
      error(err.message || 'Falha ao criar novo chip');
    } finally {
      setIsCreatingChip(false);
    }
  };

  const handleConnectChip = async (chip: WhatsAppInstance) => {
    if (!token) return;
    try {
      const conn = await connectWhatsAppInstance(token, chip.id);
      if (conn.qrcode_base64) {
        setNewChipName(chip.instance_name);
        setNewChipQuota(chip.daily_quota);
        setNewChipQrCode(conn.qrcode_base64);
        setNewChipPairingCode(conn.pairing_code || null);
        setIsNewChipDrawerOpen(true);
      } else if (conn.connected) {
        success(`Chip ${chip.instance_name} já está conectado!`, 'Conectado');
        setInstances((prev) =>
          prev.map((c) =>
            c.id === chip.id ? { ...c, status: 'CONNECTED' } : c
          )
        );
      }
    } catch (err: any) {
      error(err.message || 'Falha ao conectar chip');
    }
  };

  const handleDisconnectChip = async (chipId: string) => {
    if (!token) return;
    try {
      await disconnectWhatsAppInstance(token, chipId);
      success('Chip desconectado do pool.', 'Desconectado');
      setInstances((prev) =>
        prev.map((c) =>
          c.id === chipId ? { ...c, status: 'DISCONNECTED' } : c
        )
      );
    } catch (err: any) {
      error(err.message || 'Erro ao desconectar chip');
    }
  };

  const handleDeleteChip = async (chipId: string) => {
    if (!token) return;
    try {
      await deleteWhatsAppInstance(token, chipId);
      setInstances((prev) => prev.filter((c) => c.id !== chipId));
      success('Chip removido do pool com sucesso.', 'Removido');
    } catch (err: any) {
      error(err.message || 'Erro ao remover chip');
    }
  };

  const addSessionEvent = useCallback(
    (
      event: string,
      details: string,
      eventStatus: 'online' | 'offline' | 'warning'
    ) => {
      const newEv: WhatsAppSessionEvent = {
        id: `evt-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
        event,
        details,
        status: eventStatus,
      };
      setSessionEvents((prev) => [newEv, ...prev.slice(0, 49)]);
      if (token) {
        logWhatsAppEvent(token, { event, details, status: eventStatus });
      }
    },
    [token]
  );

  const [isQrExpired, setIsQrExpired] = useState<boolean>(false);
  const qrAutoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const formatUptime = (seconds: number) => {
    if (seconds <= 0) return isConnected ? '0s' : '--';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const isServiceUnavailable =
    status?.state === 'disconnected' ||
    status?.state === 'error' ||
    Boolean(
      status?.message &&
      (status.message.toLowerCase().includes('offline') ||
        status.message.toLowerCase().includes('indisponível') ||
        status.message.toLowerCase().includes('inacessível'))
    );

  const checkStatus = useCallback(
    async (isBackground = false) => {
      if (!token) return;
      if (!isBackground) setIsLoadingStatus(true);

      try {
        const [data, cadence] = await Promise.all([
          fetchWhatsAppStatus(token),
          fetchCadenceTelemetry(token).catch(() => null),
        ]);
        setStatus(data);
        if (cadence) setCadenceTelemetry(cadence);

        const isNowConnected = data.connected;

        const connectionChanged = wasConnectedRef.current !== isNowConnected;
        if (wasConnectedRef.current === false && isNowConnected) {
          success(
            `WhatsApp ${data.phone_number || data.profile_name || ''} conectado com sucesso!`,
            'Provedor Conectado'
          );
          setQrCodeBase64(null);
          setPairingCode(null);
          if (token) {
            fetchWhatsAppEvents(token).then((events) => {
              if (events && events.length > 0) setSessionEvents(events);
            });
          }
        }

        wasConnectedRef.current = isNowConnected;
        if (onStatusChange) {
          onStatusChange(data);
        }
        if (connectionChanged) {
          window.dispatchEvent(new CustomEvent('operational-health-refresh'));
        }

        return data;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!isBackground) {
          error(msg, 'Status do Provedor');
        }
      } finally {
        if (!isBackground) setIsLoadingStatus(false);
        setIsInitialLoading(false);
      }
    },
    [token, success, error, onStatusChange, addSessionEvent]
  );

  const QR_EXPIRY_SECONDS = 60;

  const clearQrTimer = () => {
    if (qrAutoRefreshTimerRef.current) {
      clearInterval(qrAutoRefreshTimerRef.current);
      qrAutoRefreshTimerRef.current = null;
    }
  };

  const handleConnect = async (isManual = false) => {
    if (!token || isConnectingRef.current) return;
    isConnectingRef.current = true;
    if (isManual) {
      setIsQrExpired(false);
    }
    setIsConnecting(true);
    try {
      const res = await requestWhatsAppConnect(token);
      if (res.connected) {
        success(
          'Sessão do Provedor WhatsApp já está ativa.',
          'Chip Operacional'
        );
        clearQrTimer();
        setQrCodeBase64(null);
        setPairingCode(null);
        setIsQrExpired(false);
        await checkStatus(true);
      } else if (res.qrcode_base64) {
        setQrCodeBase64(res.qrcode_base64);
        setIsQrExpired(false);
        if (res.pairing_code) {
          setPairingCode(res.pairing_code);
        }
        if (isManual) {
          info('Novo QR Code gerado com sucesso.', 'Código Atualizado');
        }
        addSessionEvent(
          'QR Code Gerado',
          'Aguardando leitura óptica no celular',
          'warning'
        );
      } else {
        clearQrTimer();
        setQrCodeBase64(null);
        setPairingCode(null);
        const errorMsg =
          res.message || 'Serviço do WhatsApp temporariamente indisponível.';
        error(errorMsg, 'Provedor Offline');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Falha de Comunicação');
    } finally {
      setIsConnecting(false);
      isConnectingRef.current = false;
    }
  };

  const handleDisconnect = async () => {
    if (!token) return;
    setIsDisconnecting(true);
    try {
      const res = await requestWhatsAppDisconnect(token);
      if (res.success) {
        success('Provedor WhatsApp desconectado com sucesso.', 'Desconectado');
        clearQrTimer();
        setQrCodeBase64(null);
        setPairingCode(null);
        setIsQrExpired(false);
        setConnectionUptimeSeconds(0);
        addSessionEvent(
          'Desconexão Manual',
          'Sessão desconectada pelo operador',
          'offline'
        );
        await checkStatus(false);
      } else {
        error(res.message, 'Falha ao desconectar');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao desconectar');
    } finally {
      setIsDisconnecting(false);
      setIsDisconnectModalOpen(false);
    }
  };

  // Ciclo de vida do QR Code: exibe por 60 segundos. Se o operador não ler, expira e solicita geração de novo código manualmente
  // evitando atualizar enquanto o handshake de conexão ainda está sendo processado pela Evolution/Baileys.
  useEffect(() => {
    if (!qrCodeBase64 || status?.connected || isQrExpired) {
      clearQrTimer();
      return;
    }

    qrAutoRefreshTimerRef.current = setTimeout(() => {
      setIsQrExpired(true);
    }, QR_EXPIRY_SECONDS * 1000);

    return () => {
      clearQrTimer();
    };
  }, [qrCodeBase64, status?.connected, isQrExpired]);

  // Carrega status inicial sem disparar QR code antes de ter certeza
  useEffect(() => {
    checkStatus(false).then((data) => {
      // Apenas sugere pareamento se a API responder estado que realmente requer leitura óptica
      if (data && !data.connected && data.state === 'connecting') {
        handleConnect();
      }
    });
  }, []);

  // Polling automático inteligente:
  // - A cada 2s quando desconectado (detecta leitura do QR code imediatamente)
  // - A cada 2s logo após conectar se o perfil (nome/foto) ainda estiver sendo hidratado pela Evolution API
  // - A cada 10s quando conectado e perfil estabilizado
  useEffect(() => {
    const isConnected = status?.connected;
    const isProfilePending =
      isConnected && (!status?.profile_name || !status?.profile_picture_url);

    if (!isInitialLoading) {
      const pollInterval = !isConnected || isProfilePending ? 2000 : 10000;
      pollTimerRef.current = setInterval(() => {
        checkStatus(true);
      }, pollInterval);
    } else if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [
    isInitialLoading,
    status?.connected,
    status?.profile_name,
    status?.profile_picture_url,
    checkStatus,
  ]);

  // Contador de uptime baseado em connected_at persistido da API
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (status?.connected) {
      const calculateSeconds = () => {
        if (status.connected_at) {
          const started = new Date(status.connected_at).getTime();
          const now = Date.now();
          const diff = Math.max(0, Math.floor((now - started) / 1000));
          setConnectionUptimeSeconds(diff);
        } else {
          setConnectionUptimeSeconds((prev) => prev + 1);
        }
      };

      calculateSeconds();
      interval = setInterval(calculateSeconds, 1000);
    } else {
      setConnectionUptimeSeconds(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status?.connected, status?.connected_at]);

  const isConnected = status?.connected === true;
  const isConnectingState =
    !isConnected && (status?.state === 'connecting' || isConnecting);

  // Colunas para a tabela de histórico com paginação (limpa sem coluna estática de status)
  const eventColumns: Column<WhatsAppSessionEvent>[] = [
    {
      key: 'timestamp',
      header: 'Horário',
      className: 'w-24 font-mono text-micro text-content-muted',
      render: (item) => item.timestamp,
    },
    {
      key: 'event',
      header: 'Evento',
      render: (item) => (
        <span className="font-semibold text-content-main">{item.event}</span>
      ),
    },
    {
      key: 'details',
      header: 'Detalhes Operacionais',
      render: (item) => (
        <span className="text-xs text-content-muted">{item.details}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. PageHeader Estrutural */}
      <PageHeader
        title="Provedor WhatsApp"
        subtitle="Gerenciamento de conexão, pareamento por QR Code e telemetria da linha"
        badge={
          <Badge
            variant={
              isConnected ? 'online' : isConnectingState ? 'warning' : 'offline'
            }
            withDot
            pulse={isConnected || isConnectingState}
          >
            {isConnected
              ? 'Linha Ativa'
              : isConnectingState
                ? 'Conectando...'
                : isServiceUnavailable
                  ? 'Provedor Offline'
                  : 'Desconectado'}
          </Badge>
        }
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Terminal className="h-3.5 w-3.5" />}
              onClick={() => setIsLogsDrawerOpen(true)}
            >
              Inspecionar Logs
            </Button>
            <Button
              variant="secondary"
              size="sm"
              isLoading={isLoadingStatus}
              leftIcon={
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isLoadingStatus ? 'animate-spin' : ''}`}
                />
              }
              onClick={() => checkStatus(false)}
            >
              Atualizar
            </Button>
          </>
        }
      />

      {/* 2. Grid de Telemetria Operacional */}
      {isInitialLoading && !status ? (
        <MetricGridSkeleton count={4} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          <MetricWidget
            label="Disparos Hoje"
            value={
              isConnected ? `${cadenceTelemetry?.dispatches_today ?? 0}` : '--'
            }
            description="Envios na janela comercial diária"
            icon={Send}
          />
          <MetricWidget
            label="Tempo Ativo (Uptime)"
            value={formatUptime(connectionUptimeSeconds)}
            description={
              isConnected ? 'Sessão contínua ativa' : 'Sessão inativa'
            }
            icon={Clock}
            trend={
              isConnected
                ? { value: 'Estável', positive: true }
                : { value: 'Inativo', positive: false }
            }
          />

          <MetricWidget
            label="Taxa de Entrega"
            value={
              cadenceTelemetry && cadenceTelemetry.dispatches_today > 0
                ? `${Math.max(0, 100 - Math.round((cadenceTelemetry.failed_count / (cadenceTelemetry.dispatches_today + cadenceTelemetry.failed_count || 1)) * 100))}%`
                : isConnected
                  ? '100%'
                  : '--'
            }
            description={
              cadenceTelemetry && cadenceTelemetry.failed_count > 0
                ? `${cadenceTelemetry.failed_count} falha(s) retida(s)`
                : 'Envios entregues sem erros'
            }
            icon={CheckCircle2}
            trend={
              isConnected ? { value: 'Normal', positive: true } : undefined
            }
          />

          <MetricWidget
            label="Status do Socket"
            value={
              isConnected
                ? 'Aberto'
                : isConnectingState
                  ? 'Sincronizando'
                  : isServiceUnavailable
                    ? 'Offline'
                    : 'Aguardando'
            }
            description="Status do Módulo WhatsApp"
            icon={Activity}
            trend={
              isConnected
                ? { value: 'Operacional', positive: true }
                : isConnectingState
                  ? { value: 'Pareando', positive: true }
                  : undefined
            }
          />
        </div>
      )}

      {/* 3. Card Principal de Conexão ou Skeleton durante carregamento inicial */}
      {isInitialLoading && !status ? (
        <WhatsAppConnectionCardSkeleton />
      ) : (
        <Card>
          <Card.Header
            title={
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-surface-border bg-surface-subtle text-content-main">
                  <Smartphone className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-semibold text-content-main">
                  {isConnected
                    ? 'Linha Telefônica Pareada'
                    : 'Parear Dispositivo WhatsApp Web'}
                </h3>
              </div>
            }
            action={
              isConnected && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<LogOut className="h-3.5 w-3.5" />}
                  onClick={() => setIsDisconnectModalOpen(true)}
                >
                  Desconectar Linha
                </Button>
              )
            }
          />

          <Card.Body className="p-6">
            {isConnected ? (
              /* ESTADO CONECTADO */
              <div className="flex flex-col items-center justify-between gap-6 sm:flex-row sm:items-start">
                <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
                  {status?.profile_picture_url ? (
                    <img
                      src={status.profile_picture_url}
                      alt="Avatar WhatsApp"
                      className="shadow-xs h-16 w-16 rounded-full border-2 border-brand-500/40 object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-brand-500/20 bg-brand-500/10 font-bold text-brand-600 dark:text-brand-400">
                      <Phone className="h-7 w-7" />
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-2 sm:justify-start">
                      <h4 className="text-base font-bold text-content-main">
                        {status?.profile_name ||
                          status?.phone_number ||
                          'Aparelho Conectado'}
                      </h4>
                      <Badge variant="online" withDot pulse>
                        Online
                      </Badge>
                    </div>

                    {status?.phone_number ? (
                      <p className="font-mono text-xs font-medium text-content-muted">
                        {status.phone_number}
                      </p>
                    ) : (
                      <p className="flex items-center justify-center gap-1.5 text-xs text-content-muted sm:justify-start">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
                        <span>Sincronizando dados da linha...</span>
                      </p>
                    )}

                    <div className="flex items-center justify-center gap-3 pt-1 text-micro text-content-muted sm:justify-start">
                      <span className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-brand-500" />
                        Instância:{' '}
                        <code className="text-content-main">
                          {status?.instance_name}
                        </code>
                      </span>
                      <span>•</span>
                      <span>Socket: Estável</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Terminal className="h-3.5 w-3.5" />}
                    onClick={() => setIsLogsDrawerOpen(true)}
                  >
                    Ver Diagnóstico
                  </Button>
                </div>
              </div>
            ) : isServiceUnavailable ? (
              /* ESTADO DE SERVIÇO INDISPONÍVEL */
              <div className="mx-auto max-w-md space-y-4 px-4 py-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-6 w-6" />
                </div>

                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-content-main">
                    Serviço do WhatsApp Temporariamente Indisponível
                  </h4>
                  {/* <p className="text-xs leading-relaxed text-content-muted">
                  Não foi possível estabelecer conexão com o serviço de
                  mensageria no momento.
                </p> */}
                </div>

                <div className="space-y-2 rounded-xl border border-surface-border bg-surface-subtle p-3.5 text-center">
                  <p className="text-xs text-content-muted">
                    O módulo pode estar em manutenção. Caso a indisponibilidade
                    persista, entre em contato com nosso suporte.
                  </p>
                  <a
                    href="https://wa.me/553182973810?text=Ol%C3%A1!%20Meu%20m%C3%B3dulo%20de%20WhatsApp%20no%20TrafficForge%20est%C3%A1%20indispon%C3%ADvel%20e%20preciso%20de%20suporte."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg p-1 text-xs font-semibold text-brand-500 transition-colors hover:text-brand-400"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    <span>Falar com o Suporte (+55 31 8297-3810)</span>
                  </a>
                </div>

                <div className="flex items-center justify-center gap-2 pt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={isLoadingStatus}
                    leftIcon={
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${isLoadingStatus ? 'animate-spin' : ''}`}
                      />
                    }
                    onClick={() => checkStatus(false)}
                  >
                    Tentar Novamente
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Terminal className="h-3.5 w-3.5" />}
                    onClick={() => setIsLogsDrawerOpen(true)}
                  >
                    Verificar Logs
                  </Button>
                </div>
              </div>
            ) : (
              /* ESTADO DESCONECTADO PRONTO PARA PAREAMENTO */
              <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-12">
                {/* Tutorial à esquerda */}
                <div className="space-y-4 md:col-span-7">
                  <h4 className="text-sm font-semibold text-content-main">
                    Instruções de Pareamento
                  </h4>

                  <StepList
                    steps={[
                      <span>
                        Abra o aplicativo do <strong>WhatsApp</strong> no
                        celular que fará os disparos.
                      </span>,
                      <span>
                        Toque no menu <strong>Mais opções</strong> (Android) ou{' '}
                        <strong>Configurações</strong> (iPhone).
                      </span>,
                      <span>
                        Toque em <strong>Aparelhos Conectados</strong> e depois
                        em <strong>Conectar um Aparelho</strong>.
                      </span>,
                      <span>
                        Aponte a câmera do celular para o QR Code ao lado. A
                        sessão e o perfil do chip sincronizam em poucos
                        segundos.
                      </span>,
                    ]}
                  />
                </div>

                {/* QR Code à direita */}
                <div className="flex flex-col items-center justify-center rounded-2xl border border-surface-border bg-surface-subtle p-4 sm:p-6 md:col-span-5">
                  {isConnecting && !qrCodeBase64 ? (
                    <div className="space-y-3 py-12 text-center">
                      <Spinner size="md" color="brand" />
                      <p className="text-xs text-content-muted">
                        Gerando QR Code...
                      </p>
                    </div>
                  ) : qrCodeBase64 ? (
                    <div className="flex w-full flex-col items-center space-y-4">
                      {isQrExpired ? (
                        <div className="flex w-full flex-col items-center justify-center space-y-3 rounded-2xl border border-dashed border-surface-border bg-surface-panel p-6 text-center">
                          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                            <Clock className="h-6 w-6" />
                          </div>
                          <div className="space-y-1">
                            <Badge variant="warning">QR Code Expirado</Badge>
                            <p className="text-xs text-content-muted">
                              O código expirou por inatividade para sua
                              segurança.
                            </p>
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                            onClick={() => handleConnect(true)}
                            isLoading={isConnecting}
                            className="w-full sm:w-auto"
                          >
                            Gerar Novo QR Code
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="shadow-xs flex items-center justify-center rounded-2xl border border-surface-border bg-white p-3">
                            <img
                              src={
                                qrCodeBase64.startsWith('data:')
                                  ? qrCodeBase64
                                  : `data:image/png;base64,${qrCodeBase64}`
                              }
                              alt="QR Code WhatsApp"
                              className="h-48 w-48 object-contain sm:h-52 sm:w-52"
                            />
                          </div>

                          {pairingCode && (
                            <div className="text-center">
                              <span className="text-micro text-content-muted">
                                Código de pareamento:{' '}
                              </span>
                              <span className="font-mono text-xs font-bold text-content-main">
                                {pairingCode}
                              </span>
                            </div>
                          )}

                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={
                              <RefreshCw
                                className={`h-3.5 w-3.5 ${isConnecting || status?.state === 'connecting' ? 'animate-spin' : ''}`}
                              />
                            }
                            onClick={() => handleConnect(true)}
                            disabled={
                              isConnecting || status?.state === 'connecting'
                            }
                            className="w-full"
                          >
                            {status?.state === 'connecting'
                              ? 'Conectando ao WhatsApp... (Aguarde até 60s)'
                              : 'Atualizar QR Code'}
                          </Button>

                          <div className="flex items-center gap-2 text-2xs text-content-muted">
                            <span
                              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                status?.state === 'connecting'
                                  ? 'animate-ping bg-amber-500'
                                  : 'animate-pulse bg-brand-500'
                              }`}
                            />
                            <span>
                              {status?.state === 'connecting'
                                ? 'Sincronização em andamento com a Evolution API! O pareamento pode levar até 60 segundos. Por favor, não feche nem recarregue a página.'
                                : 'Aguardando leitura óptica... O código expira automaticamente se não for lido.'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3 py-10 text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-surface-border bg-surface-panel text-content-muted">
                        <QrCode className="h-6 w-6" />
                      </div>
                      <p className="text-xs text-content-muted">
                        Pronto para gerar o código de conexão.
                      </p>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleConnect(false)}
                        isLoading={isConnecting}
                      >
                        Gerar QR Code
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* 4. Pool Global de Chips (Revezamento Round-Robin & Anti-Colisão) */}
      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-content-main">
              Pool Global de Chips WhatsApp
            </h3>
            <p className="text-xs text-content-muted">
              Revezamento inteligente (Round-Robin), monitoramento de cotas
              diárias e failover automático
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => {
              setNewChipName('');
              setNewChipQuota(50);
              setNewChipQrCode(null);
              setNewChipPairingCode(null);
              setIsNewChipDrawerOpen(true);
            }}
          >
            Conectar Novo Chip
          </Button>
        </div>

        {instances.length === 0 ? (
          <Card>
            <Card.Body className="space-y-3 p-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-surface-border bg-surface-subtle text-content-muted">
                <Smartphone className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-content-main">
                Nenhum chip secundário conectado ao pool
              </p>
              <p className="mx-auto max-w-sm text-xs text-content-muted">
                Conecte 2 ou mais números de WhatsApp para distribuir os
                disparos das suas filas e proteger suas linhas.
              </p>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => {
                  setNewChipName('');
                  setNewChipQuota(50);
                  setNewChipQrCode(null);
                  setNewChipPairingCode(null);
                  setIsNewChipDrawerOpen(true);
                }}
              >
                Adicionar Chip ao Pool
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {instances.map((chip) => {
              const isChipConnected = chip.status.toUpperCase() === 'CONNECTED';
              const isChipConnecting =
                chip.status.toUpperCase() === 'CONNECTING';
              const usedQuotaPercent = Math.min(
                100,
                Math.round(
                  (chip.daily_sent_count / Math.max(chip.daily_quota, 1)) * 100
                )
              );

              return (
                <Card key={chip.id}>
                  <Card.Header
                    title={
                      <div className="flex items-center gap-2">
                        <Smartphone className="h-4 w-4 text-brand-500" />
                        <span className="text-sm font-semibold text-content-main">
                          {chip.instance_name}
                        </span>
                      </div>
                    }
                    action={
                      isChipConnected ? (
                        <Badge variant="online" withDot pulse>
                          Online
                        </Badge>
                      ) : isChipConnecting ? (
                        <Badge variant="warning" withDot>
                          Pareando
                        </Badge>
                      ) : (
                        <Badge variant="offline" withDot>
                          Offline
                        </Badge>
                      )
                    }
                  />
                  <Card.Body className="space-y-4 p-4">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-content-muted">
                        {chip.phone_number || 'Telefone não vinculado'}
                      </span>
                      <div className="flex items-center gap-1 text-content-muted">
                        <Battery className="h-3.5 w-3.5 text-brand-500" />
                        <span>{`${chip.battery_level ?? 100}%`}</span>
                      </div>
                    </div>

                    {/* Barra de Cota Diária */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-micro text-content-muted">
                        <span>Cota Diária de Envios</span>
                        <span className="font-semibold text-content-main">
                          {`${chip.daily_sent_count} / ${chip.daily_quota} msgs`}
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full border border-surface-border bg-surface-subtle">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            usedQuotaPercent >= 90
                              ? 'bg-red-500'
                              : usedQuotaPercent >= 70
                                ? 'bg-amber-500'
                                : 'bg-brand-500'
                          }`}
                          style={{ width: `${usedQuotaPercent}%` }}
                        />
                      </div>
                      <div className="flex justify-end text-2xs text-content-muted">
                        <span>{`${usedQuotaPercent}% utilizado hoje`}</span>
                      </div>
                    </div>

                    {/* Ações do Chip */}
                    <div className="flex items-center justify-end gap-2 border-t border-surface-border pt-2">
                      {isChipConnected ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={
                            <LogOut className="h-3.5 w-3.5 text-red-500" />
                          }
                          onClick={() => handleDisconnectChip(chip.id)}
                        >
                          Desconectar
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={
                            <QrCode className="h-3.5 w-3.5 text-brand-500" />
                          }
                          onClick={() => handleConnectChip(chip)}
                        >
                          Conectar QR Code
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={
                          <Trash2 className="h-3.5 w-3.5 text-content-muted" />
                        }
                        onClick={() => handleDeleteChip(chip.id)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* 5. Tabela de Histórico de Conexões com Paginação */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* <FileText className="h-4 w-4 text-content-muted" /> */}
            <h3 className="text-sm font-semibold text-content-main">
              Histórico de Sessões & Conexões
            </h3>
          </div>
          {/* <span className="text-micro text-content-muted">
            Registros da sessão ativa
          </span> */}
        </div>

        {/* TODO: Integrar com endpoint paginado do backend quando a tabela de eventos for persistida */}
        <DataTable
          columns={eventColumns}
          data={sessionEvents}
          keyExtractor={(item) => item.id}
          emptyMessage="Nenhum evento registrado nesta sessão."
          pageSize={5}
        />
      </div>

      {/* 5. ConfirmationModal para Desconexão (Zero Stacked Modals) */}
      <ConfirmationModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnect}
        isLoading={isDisconnecting}
        title="Desconectar Provedor WhatsApp?"
        variant="danger"
        confirmText="Sim, Desconectar"
        cancelText="Manter Conectado"
        description="A esteira automática de disparos e a sincronização de grupos ficarão pausadas até que uma nova linha seja conectada."
      />

      {/* 6. Drawer Lateral de Inspeção de Logs e Diagnóstico */}
      <Drawer
        isOpen={isLogsDrawerOpen}
        onClose={() => setIsLogsDrawerOpen(false)}
        title="Diagnóstico do Provedor WhatsApp"
        description="Inspeção técnica de sockets, conectividade e parâmetros"
        width="lg"
        footer={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsLogsDrawerOpen(false)}
          >
            Fechar Diagnóstico
          </Button>
        }
      >
        <div className="space-y-5">
          {/* Status do Provedor */}
          <div className="space-y-2 rounded-xl border border-surface-border bg-surface-subtle p-3.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-content-main">
                Status da Conexão
              </span>
              <Badge variant={isConnected ? 'online' : 'offline'} withDot>
                {isConnected
                  ? 'Socket Aberto'
                  : isServiceUnavailable
                    ? 'Serviço Offline'
                    : 'Socket Fechado'}
              </Badge>
            </div>
            <p className="text-micro text-content-muted">
              Instância:{' '}
              <code className="text-content-main">
                {status?.instance_name || 'affiliator_default'}
              </code>
            </p>
            {status?.message && (
              <p className="text-micro text-content-muted">
                Mensagem: {status.message}
              </p>
            )}
          </div>

          {/* Parâmetros Operacionais */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-content-main">
              Parâmetros Operacionais
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                { label: 'Intervalo de Jitter', value: '240s - 480s' },
                { label: 'Janela Operacional', value: '08:00 às 22:00' },
                { label: 'Teto de Grupos', value: '1.000 membros' },
                { label: 'Taxa de Erros', value: '0.0%' },
              ].map((param) => (
                <div
                  key={param.label}
                  className="rounded-lg border border-surface-border bg-surface-subtle p-2.5"
                >
                  <span className="block text-2xs text-content-muted">
                    {param.label}
                  </span>
                  <span className="font-mono font-semibold text-content-main">
                    {param.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Console de Eventos Recentes */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-content-main">
              Log de Eventos
            </h4>
            <div className="max-h-60 space-y-1.5 overflow-y-auto rounded-xl border border-surface-border bg-surface-subtle p-3 font-mono text-micro">
              {sessionEvents.length === 0 ? (
                <p className="py-4 text-center text-xs text-content-muted">
                  Nenhum evento registrado nesta sessão.
                </p>
              ) : (
                sessionEvents.map((evt) => (
                  <div key={evt.id} className="flex items-start gap-2">
                    <span className="select-none text-content-muted">
                      [{evt.timestamp}]
                    </span>
                    <span
                      className={
                        evt.status === 'online'
                          ? 'text-brand-500'
                          : evt.status === 'warning'
                            ? 'text-amber-500'
                            : 'text-red-500'
                      }
                    >
                      •
                    </span>
                    <span className="text-content-main">{evt.event}:</span>
                    <span className="text-content-muted">{evt.details}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* 7. Drawer Lateral para Conectar Novo Chip (QR Code / Pareamento) */}
      <Drawer
        isOpen={isNewChipDrawerOpen}
        onClose={() => {
          setIsNewChipDrawerOpen(false);
          setNewChipQrCode(null);
          setNewChipPairingCode(null);
        }}
        title="Conectar Novo Chip WhatsApp"
        description="Adicione múltiplos números como administradores dos seus grupos para revezamento (Round-Robin) e failover automático."
        width="md"
        footer={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setIsNewChipDrawerOpen(false);
              setNewChipQrCode(null);
              setNewChipPairingCode(null);
            }}
          >
            Fechar
          </Button>
        }
      >
        <div className="space-y-5">
          <Input
            label="Identificador do Chip"
            placeholder="Ex: Chip Admin 02"
            value={newChipName}
            onChange={(e) => setNewChipName(e.target.value)}
            helperText="Nome para identificação interna nos relatórios e revezamento."
          />

          <Input
            label="Cota Diária de Envios (mensagens)"
            type="number"
            min={10}
            max={500}
            value={newChipQuota}
            onChange={(e) => setNewChipQuota(Number(e.target.value))}
            helperText="Limite seguro recomendado: 50 a 100 mensagens por chip."
          />

          {newChipQrCode ? (
            <div className="flex flex-col items-center space-y-4 rounded-2xl border border-surface-border bg-surface-subtle p-6 text-center">
              <div className="shadow-xs rounded-xl border border-surface-border bg-white p-3">
                <img
                  src={
                    newChipQrCode.startsWith('data:')
                      ? newChipQrCode
                      : `data:image/png;base64,${newChipQrCode}`
                  }
                  alt="QR Code Chip"
                  className="h-48 w-48 object-contain"
                />
              </div>

              {newChipPairingCode && (
                <div className="text-center">
                  <span className="text-micro text-content-muted">
                    Código de pareamento:{' '}
                  </span>
                  <span className="font-mono text-xs font-bold text-content-main">
                    {newChipPairingCode}
                  </span>
                </div>
              )}

              <p className="text-xs text-content-muted">
                Abra o WhatsApp no aparelho deste chip &gt; Aparelhos Conectados
                &gt; Conectar um Aparelho e aponte para o código acima.
              </p>
            </div>
          ) : (
            <Button
              variant="primary"
              size="md"
              className="w-full"
              isLoading={isCreatingChip}
              leftIcon={<QrCode className="h-4 w-4" />}
              onClick={handleCreateAndPairNewChip}
            >
              Gerar QR Code de Conexão
            </Button>
          )}
        </div>
      </Drawer>
    </div>
  );
};
